import React from 'react'
import { formatCurrency } from 'utils/currencyFormatter'
import { useWelcomeCouponOffer } from 'hooks'

// Welcome-coupon pill for the community pages, and the held-coupon reminder on
// every team page. On the public team page a claimable viewer sees the offer
// strips instead (WelcomeCouponOffer), so that page passes claimable={false}.
// The claim flow and the impression live in useWelcomeCouponOffer. Rendered
// `position: fixed` at `z-[10000]` (above every modal/lightbox/flash layer a
// visitor can reach). It sits at the very top center at every width: mobile
// `top-2`, width-constrained to clear the modal corner close/back/share
// controls (it MAY overlap the centered title label, never a control); desktop
// `lg:top-1`, above every modal, which are centred and start at 5vh. A fixed
// offset below the header band cannot work for modals of different heights.
// `data-welcome-coupon-pill` lets #flash-messages move below it (see
// application.tailwind.css). It is NOT dismissible. It is suppressed ONLY
// while its own login modal or the full-discount confirm dialog is open (see
// `suppressed`).
//
// CTA branches on auth:
// - logged-out → records welcome-coupon intent, then opens the login/registration
//   modal via onClaim (the unauthenticated registration path).
// - logged-in → calls the claim endpoint directly (never the intent endpoint,
//   never the login modal); on success the pill flips to a claimed confirmation
//   that names the expiry date.
//
// `held` is the unused coupon the viewer already holds ({ amount, currency,
// expiresOn }, server-formatted date). While it is set the pill stays on the
// page as a status saying how much is left and until when.
export default function WelcomeCouponBanner({ team, currentUser, teamSlug, translations, onClaim, suppressed = false, claimable = false, held = null, colors }) {
  const amount = Number(team?.welcome_coupon_amount)
  const currency = team?.welcome_coupon_currency || 'TWD'
  const { status, coupon, claim } = useWelcomeCouponOffer({ team, currentUser, teamSlug, claimable, held, onClaim, placement: 'top_pill' })

  const fillAmountAndDate = (template, coupon) =>
    (template || '')
      .replace('%{amount}', formatCurrency(Number(coupon.amount), coupon.currency || currency))
      .replace('%{date}', coupon.expiresOn || '')
  const ctaTemplate = currentUser
    ? (translations?.welcomeCoupon?.claimCta || translations?.welcomeCoupon?.cta || '')
    : (translations?.welcomeCoupon?.cta || '')
  const label = ctaTemplate.replace('%{amount}', formatCurrency(amount, currency))

  // `suppressed` hides every state: the claim button while its own login modal
  // is open, and the held reminder while the full-discount confirm dialog says
  // how much of this coupon will be voided.
  if (suppressed) return null

  // Just claimed: a confirmation with the date. Already held: a reminder of
  // the amount left and the date. Both are statuses, not buttons.
  const statusLabel = coupon
    ? fillAmountAndDate(status === 'claimed' ? translations?.welcomeCoupon?.claimed : translations?.welcomeCoupon?.held, coupon)
    : ''
  if (statusLabel.trim()) {
    return (
      <div
        role="status"
        aria-label={statusLabel}
        data-welcome-coupon-pill
        className="fixed top-2 lg:top-1 left-1/2 -translate-x-1/2 z-[10000] px-4 max-w-[calc(100vw-7rem)] lg:max-w-full"
      >
        <div
          className="flex items-center gap-2 max-w-full rounded-full px-4 py-1.5 lg:py-1 shadow-md text-white text-xs lg:text-sm font-medium"
          style={{ backgroundColor: colors?.primary || '#3366FF' }}
        >
          <span aria-hidden="true">{status === 'claimed' ? '🎉' : '🎁'}</span>
          <span className="truncate min-w-0">{statusLabel}</span>
        </div>
      </div>
    )
  }

  // Hide while its own login modal is open, when there is nothing to claim, or
  // if the cta resolved to empty (missing translation) — never ship a blank,
  // unlabeled clickable pill.
  if (status !== 'claimable' || !label.trim()) return null

  return (
    <div
      role="region"
      aria-label={label}
      data-welcome-coupon-pill
      className="fixed top-2 lg:top-1 left-1/2 -translate-x-1/2 z-[10000] px-4 max-w-[calc(100vw-7rem)] lg:max-w-full"
    >
      <button
        type="button"
        onClick={() => claim('top_pill')}
        className="flex items-center gap-2 max-w-full rounded-full px-4 py-1.5 lg:py-1 shadow-md text-white text-xs lg:text-sm font-medium hover:opacity-90 transition-opacity"
        style={{ backgroundColor: colors?.primary || '#3366FF' }}
      >
        <span aria-hidden="true">🎁</span>
        <span className="truncate min-w-0">{label}</span>
      </button>
    </div>
  )
}
